// glare.js
// The sun-anchored glass glare affordance (docs/dom/AFFORDANCES.md): every glass tile
// (.icon-tile) anywhere in the app — the bid/discard/game-type prompt wells, the result
// strip's overlay affordances + Net chip, and the top-right Menu button (in-game and on
// the start screen) — is "a window into the game" (DESIGN principle 18), so each
// reflects the scene's one light: the **sun**, the layout's focal point (`_layout.oval`)
// at the trick area's centre — or, when there is no board (the start screen), a
// viewport golden-cut stand-in (see sunInViewport). Two layers:
//   - REST SPLAY (static, always on): each tile's catch-light sits on its sun-facing
//     edge, derived from the tile's vector to the sun. A per-tile inline --glare-angle
//     the CSS material reads. Survives reduced motion — it's position, not motion.
//   - POINTER TILT (motion): one --glare-shift value, shared by every tile, tracking the
//     pointer with a dreamy trailing ease — the whole app tilting under a moving light.
//     Computed once per frame from the app centre, then written onto each tile
//     INDIVIDUALLY rather than once on #skat-app: the property inherits, so a root write
//     would restyle all ~1245 descendants per frame (~40× the cost — see glareTick).
//     Self-gates reduced motion (no rAF, no listener); the rest splay stays.
//
// CSS owns the MATERIAL (the catch-light gradient + its brightness); this module owns the
// DIRECTION (engine-sets / CSS-consumes, css/CLAUDE.md). The .icon-tile::before reads
// calc(var(--glare-angle, 150deg) + var(--glare-shift, 0deg)); the fallbacks keep the
// shipped look if this module never runs.
//
// App-scoped since menu-on-start-screen-and-sizing (renamed from prompt-glare.js — it was
// per-container, `.prompt-zone` or the result strip). #skat-app is persistent and NEVER
// rebuilt (docs/DOM), unlike the containers this module used to arm, so the old
// "shed the prior zone's listener" dance — needed only because a from-scratch render
// replaced those containers — has no reason to exist here: the module wires its window
// listener on #skat-app exactly once, ever. Every builder (prompt-ui.js, result-ui.js,
// game-start-ui.js) calls armGlare/refreshGlareAngles on its own render/reflow; each
// call re-sweeps EVERY `.icon-tile` currently in the app, so a tile a different caller
// built still gets lit.
import { shouldAnimateReveal } from "../animate.js";

export const GLARE_DEFAULTS = {
  restMode: "hold", // play-test: 'hold' = the tilt stays where the pointer last sat (Andre's pick, 2026-06-19); 'settle' = eases back to sun-rest when idle. Lab-toggleable.
  shiftMax: 40, // play-test: max pointer-tilt swing of the whole pane (deg)
  influence: 900, // play-test: px from the app centre where the pointer tilt saturates
  ease: 0.12, // play-test: per-frame lerp toward the target shift (lower = dreamier trail)
  verticalBias: 0.35, // play-test: how much vertical pointer offset also tilts (0..1)
};

// why: settle-mode idle delay (ms) before the tilt eases back to rest — long enough that
// a pause mid-gesture isn't read as "let go". Only used when restMode === 'settle'.
const SETTLE_IDLE_MS = 600;

const SHIFT_EPSILON = 0.01; // converged-enough to snap the lerp and idle the rAF

// The start screen has no oval (no board, no sun to read a direction from) — this is
// where the sun would notionally sit: centred horizontally, 1/φ² ≈ 0.382 down the
// viewport. Golden by default (DESIGN §15) — the same fraction layout-solver.js's oval
// centre (`goldenRatio: 0.382`) and constants.js's TRICK_SLOT_ICON_RATIO/ARCH.CARD_OVERLAP
// use. Andre's explicit call: the board's absence doesn't mean no light.
const SUN_FALLBACK_Y_RATIO = 0.382;

/**
 * The pure geometry core: the CSS gradient angle (deg) that puts the bright catch-light
 * on the tile's SUN-FACING edge. CSS gradient degrees: 0 = up, clockwise; the bright stop
 * sits at 0% — OPPOSITE the gradient direction — so feeding the direction (tile → away
 * from sun) = (centre − sun) lands the highlight on the sun side. Both points viewport-space.
 */
export function glareAngleDeg(centre, sun) {
  return (Math.atan2(centre.x - sun.x, -(centre.y - sun.y)) * 180) / Math.PI;
}

function opts(appEl) {
  return { ...GLARE_DEFAULTS, ...appEl._glareOverrides };
}

// The sun in viewport-space: the board's oval focal point when one exists (read fresh,
// never cached — refreshLayout replaces _layout on every resize, so a stored layout goes
// stale), converted from app-space to viewport-space (tile rects are viewport-space and
// #skat-app's box need not sit at the viewport origin); or the golden-cut stand-in when
// there's no board (the start screen) — already viewport-space, no app-rect offset needed.
// `appRect` is handed in rather than re-measured: the caller's read pass already has it.
function sunInViewport(appEl, appRect) {
  const oval = appEl._layout?.oval;
  if (oval) {
    return { x: appRect.x + oval.cx, y: appRect.y + oval.cy };
  }
  return {
    x: window.innerWidth / 2,
    y: window.innerHeight * SUN_FALLBACK_Y_RATIO,
  };
}

// (Re)write every tile's rest splay angle from its live rect → the sun, AND cache both the
// app's centre and the TILE LIST for onGlareMove/glareTick — the same caching discipline
// this module carried before it went app-scoped (perf-gesture-geometry-cache: never re-read
// the DOM inside the pointermove write loop), now against #skat-app's rect instead of a
// per-container one. The read pass this shares with the rest-splay write is the SAME batched
// geometry read armGlare/refreshGlareAngles already perform at every arm/resize, so caching
// costs nothing extra. Two-pass per the ADR (js/animation/CLAUDE.md § measure/mutate
// discipline): map every tile's rect first, THEN write every --glare-angle — never
// interleaved.
function writeRestAngles(appEl, appRect) {
  const tiles = [...appEl.querySelectorAll(".icon-tile")];
  const rects = tiles.map((t) => t.getBoundingClientRect());
  appEl._glareTiles = tiles;
  appEl._glareCentre = {
    x: appRect.x + appRect.width / 2,
    y: appRect.y + appRect.height / 2,
  };
  const sun = sunInViewport(appEl, appRect);
  tiles.forEach((tile, i) => {
    const r = rects[i];
    const centre = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    tile.style.setProperty(
      "--glare-angle",
      glareAngleDeg(centre, sun).toFixed(1) + "deg",
    );
  });
}

/** The reflow-path angle refresh: tiles moved (a resize, a placement pass), so
 *  recompute every tile's rest splay from the fresh layout. No re-wiring — the
 *  pointer listener + rAF live on the persistent #skat-app. */
export function refreshGlareAngles(appEl) {
  writeRestAngles(appEl, appEl.getBoundingClientRect());
}

// Start the lerp loop if it isn't already running (idempotent — re-armed on each move).
function ensureRaf(appEl, st) {
  if (!st.raf) st.raf = requestAnimationFrame(() => glareTick(appEl, st));
}

// One lerp frame: ease shift → target, write --glare-shift on EACH TILE, and IDLE the
// rAF once converged (re-armed on the next pointermove) so nothing spins at rest.
//
// why write per-tile instead of once on #skat-app: --glare-shift is an inherited custom
// property, so writing it on the board root invalidates style for every descendant — and
// #skat-app has ~1245 of them. Measured on the trick-phase board (1280×800): 4.6ms per
// write on #skat-app vs 0.11ms on a tile — ~40×, on a rAF that runs on the same frames as
// card-drag and hover-lift. Trick play carried exactly ONE tile (the menu button; the
// AWAIT_CARD_PLAY prompt builds none) at measurement time — since fullscreen-on-resumed-
// session it's one or two: the Fullscreen re-entry button is a second always-mounted
// tile outside the prompt zone, but only on a device with retractable chrome to gain AND
// only while the board isn't already fullscreen
// (fullscreen-button.js#shouldShowFullscreenButton) — on the 1280×800 desktop where
// the above was measured it's still exactly one.
// Either way the loop stays near-free where it matters most, just not pinned at one.
// The write is only half the cost, though: each write also REPAINTS that tile's ::before
// catch-light, and a tile sitting on the board's big shared layer lands that raster work
// on the whole layer's budget per frame — which regressed the tablet's declaration reveal
// 4–5× until the menu button got its own compositor layer (the .menu-button will-change
// pin in skat.css; docs/PERFORMANCE.md § one-small-element). A new always-mounted tile
// outside the prompt zone needs the same look — the Fullscreen button carries the
// identical pin (skat.css § Fullscreen Button) for the identical reason.
// The tile list is the one writeRestAngles cached at the last arm/reflow — never a live
// query here (docs/PERFORMANCE § gesture loops: no DOM reads on the move path).
function glareTick(appEl, st) {
  const o = opts(appEl);
  st.shift += (st.target - st.shift) * o.ease;
  const done = Math.abs(st.target - st.shift) < SHIFT_EPSILON;
  if (done) st.shift = st.target; // snap so the held value is exact
  const shift = st.shift.toFixed(2) + "deg";
  for (const tile of appEl._glareTiles)
    tile.style.setProperty("--glare-shift", shift);
  st.raf = done ? 0 : requestAnimationFrame(() => glareTick(appEl, st));
}

// Map the pointer's offset from the app centre to a target shift (horizontal dominant, a
// verticalBias slice of vertical travel), saturating at influence px. 'settle' re-centres
// the target to rest after the pointer idles; 'hold' leaves it where the pointer last sat.
// The centre is read from the cache writeRestAngles wrote at the last arm/reflow — this
// fires on every window pointermove while the app is armed, so a live
// getBoundingClientRect here would force a layout read per move.
function onGlareMove(appEl, st, e) {
  const c = appEl._glareCentre;
  if (!c) return;
  const o = opts(appEl);
  const hx = (e.clientX - c.x) / o.influence;
  const vy = ((e.clientY - c.y) / o.influence) * o.verticalBias;
  st.target = Math.max(-1, Math.min(1, hx + vy)) * o.shiftMax;
  if (o.restMode === "settle") {
    clearTimeout(st.idleTimer);
    st.idleTimer = setTimeout(() => {
      st.target = 0;
      ensureRaf(appEl, st);
    }, SETTLE_IDLE_MS);
  }
  ensureRaf(appEl, st);
}

// Wire the pointer-tilt layer onto #skat-app (once — guarded by the caller).
function wirePointerTilt(appEl) {
  const st = (appEl._glare = {
    raf: 0,
    shift: 0,
    target: 0,
    move: null,
    idleTimer: 0,
  });
  st.move = (e) => onGlareMove(appEl, st, e);
  window.addEventListener("pointermove", st.move);
}

/**
 * Arm the glare on #skat-app. Idempotent — every builder (prompt-ui.js, result-ui.js,
 * game-start-ui.js) calls this on its own render/reflow: it (re)writes EVERY tile's
 * --glare-angle in the app every time (cheap — a handful of tiles), and wires the
 * pointer-tilt listener + rAF on #skat-app ONCE for the app's whole lifetime (guarded by
 * `_glareArmed` — #skat-app is persistent, never rebuilt, so there is no "prior zone" to
 * shed the way this module had to before it went app-scoped). `overrides` patches
 * GLARE_DEFAULTS (the lab's live knobs). Reduced motion: the static splay is still
 * written; only the pointer layer gates off.
 */
export function armGlare(appEl, overrides) {
  appEl._glareOverrides = overrides;
  writeRestAngles(appEl, appEl.getBoundingClientRect());
  // Reduced motion: rest splay only, no pointer layer. RELEASE rather than just skip the
  // wiring — shouldAnimateReveal() re-reads matchMedia per call, so the preference can flip
  // mid-session, and #skat-app's persistence means no zone swap sheds a stale listener the
  // way it did before this module went app-scoped. This is that shedding path, made explicit.
  if (!shouldAnimateReveal()) {
    releaseGlare(appEl);
    return;
  }
  if (appEl._glareArmed) return; // listener already wired
  appEl._glareArmed = true;
  wirePointerTilt(appEl);
}

/**
 * The cleanup seam: cancel the rAF, drop the pointer listener, and clear each tile's
 * --glare-shift so they land back on their CSS resting splay (the --glare-angle rest
 * splay itself stays — it's position, not motion). Idempotent. Reached from armGlare's
 * reduced-motion branch; exported so a teardown path can shed the layer directly.
 */
export function releaseGlare(appEl) {
  const st = appEl._glare;
  appEl._glareArmed = false;
  appEl._glareCentre = null;
  for (const tile of appEl._glareTiles ?? [])
    tile.style.removeProperty("--glare-shift");
  if (!st) return;
  if (st.raf) cancelAnimationFrame(st.raf);
  clearTimeout(st.idleTimer);
  window.removeEventListener("pointermove", st.move);
  appEl._glare = null;
}
